import React from "react";
import {
  Box,
  Button,
  ClickAwayListener,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import NavigateSettingsIcon from "../../assets/interaction-detail-page/navigate-settings-icon.svg";
import UserInteractionsService from "../../services/interactions/Interactions";
import "../../styles/templateBasedExtraction.css";
import {
  ExistingTemplatesPopperProps,
  TemplateItem,
} from "../../interfaces/templateBasedExtraction";
import { useNavigate } from "react-router-dom";
export type { TemplateItem };

const ExistingTemplatesPopper: React.FC<ExistingTemplatesPopperProps> = ({
  open,
  anchorEl,
  onClose,
  isEnabled = true,
  templates,
  isLoading: propIsLoading,
  onEditTemplate,
  onRunTemplate,
  onViewTemplateExtraction,
  onCreateNewTemplate,
  collaterals = [],
  selectedRecording,
  offset = [0, 6],
}) => {
  const navigate = useNavigate();
  const { data: templateListData, isLoading: isTemplatesQueryLoading } =
    UserInteractionsService.useGetTemplateList(
      {},
      templates === undefined && isEnabled && open,
    );

  const resolvedAnchorEl = React.useMemo(() => {
    if (!anchorEl) return null;
    if (anchorEl instanceof HTMLElement) {
      const labelEl =
        anchorEl.querySelector(".template-generate-label") ||
        anchorEl.querySelector(".tab-label-box");
      if (labelEl instanceof HTMLElement) {
        return labelEl;
      }
    }
    return anchorEl;
  }, [anchorEl]);

  const resolvedTemplates: TemplateItem[] = React.useMemo(() => {
    const rawList = templates ?? (Array.isArray(templateListData)
      ? templateListData
      : Array.isArray(templateListData?.data)
        ? templateListData.data
          : []);

    return rawList
      .map((item: any) => {
        const id = String(item.id);
        const extraction = collaterals.find((collateral: any) => {
          if (collateral.assetType !== "template_based_extraction_v2") return false;
          if (selectedRecording && collateral.interactionGroupId !== selectedRecording) return false;
          return String(collateral.assetMetaData?.templateId || "") === id;
        });
        return {
          ...item,
          id,
          name: item.templateName,
          prompt: item.templatePrompt,
          scope: item.templateScope,
          isOwner: item.isOwner === true,
          ...(extraction && {
            assetId: extraction.interactionAssetId,
            templateId: id,
            interactionGroupId: extraction.interactionGroupId,
            aiAnalysisStatus: extraction.aiAnalysisStatus || extraction.status,
          }),
        };
      })
      .filter((item: TemplateItem) => Boolean(item.id));
  }, [templates, templateListData, collaterals, selectedRecording]);

  const isLoading =
    propIsLoading !== undefined ? propIsLoading : isTemplatesQueryLoading;

  return (
    <Popper
      open={open}
      anchorEl={resolvedAnchorEl}
      placement="right-start"
      sx={{ zIndex: 1300 }}
      modifiers={[
        {
          name: "offset",
          options: {
            offset: offset || [0, 6],
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper elevation={4} className="existing-templates-popper-paper">
          {/* Header */}
          <Box className="existing-templates-header">
            <Typography className="existing-templates-title">
              Existing templates
            </Typography>
            <Tooltip title="Settings" arrow>
              <IconButton
                size="small"
                sx={{ p: 0.5 }}
                onClick={() => navigate("/settings/user-settings?tab=TEMPLATES")}
              >
                <img
                  src={NavigateSettingsIcon}
                  alt="settings"
                  className="existing-templates-settings-icon"
                />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider className="existing-templates-divider" />

          {/* Template Items */}
          <Box className="existing-templates-list-container">
            {isLoading &&
            (!resolvedTemplates || resolvedTemplates.length === 0) ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={24} sx={{ color: "#6036D6" }} />
              </Box>
            ) : (
              <Box className="existing-templates-list">
                {resolvedTemplates.map((tmpl, index) => (
                  <React.Fragment key={tmpl.id}>
                    <Box className="existing-templates-item">
                      <Typography className="existing-templates-item-name">
                        {tmpl.name}
                      </Typography>
                      <Box className="existing-templates-item-actions">
                        {tmpl.isOwner && (
                          <Tooltip title="Edit template" arrow>
                            <Box
                              className="existing-templates-edit-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditTemplate?.(tmpl);
                              }}
                            >
                              <EditOutlinedIcon
                                className="existing-templates-edit-icon"
                                sx={{ fontSize: "14px" }}
                              />
                            </Box>
                          </Tooltip>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (tmpl.assetId) {
                              onViewTemplateExtraction?.(tmpl);
                            } else {
                              onRunTemplate(tmpl);
                            }
                          }}
                          className={
                            tmpl.assetId
                              ? "existing-templates-view-btn"
                              : "existing-templates-run-btn"
                          }
                        >
                          {tmpl.assetId ? "View" : "Run"}
                        </Button>
                      </Box>
                    </Box>
                    {index < resolvedTemplates.length - 1 && (
                      <Divider className="existing-templates-divider" />
                    )}
                  </React.Fragment>
                ))}
              </Box>
            )}
          </Box>

          <Divider className="existing-templates-divider" />

          {/* Create new template card */}
          <Box
            onClick={onCreateNewTemplate}
            className="existing-templates-create-card"
          >
            <Box className="existing-templates-create-icon">
              <AddIcon sx={{ color: "#FFF", fontSize: "20px" }} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography className="existing-templates-create-title">
                Create new template
              </Typography>
              <Typography className="existing-templates-create-subtitle">
                Define your own extraction
              </Typography>
            </Box>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default ExistingTemplatesPopper;
